import { JobType, PersonJobType } from '@api';
import { ShowTableCell } from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import { Date } from '../../../../common';
import { UnconfirmedItem } from './components';

const TRANS_PATH = 'TOUR_REP_SELF_PROMOTE.TABLE_COLUMNS';

export type UseColumnsArgsType = {
  checkedJobIds: string[];
  onCheckJobId: (jobId: string, isChecked: boolean) => void;
  isEditingOwnUser: boolean;
};

export type UseColumnsReturnType = {
  columns: DataTableColumn<PersonJobType>[];
};

export const useColumns = ({
  checkedJobIds,
  onCheckJobId,
  isEditingOwnUser,
}: UseColumnsArgsType): UseColumnsReturnType => {
  const { t } = useTranslation('personDetailsTourRep');

  const isChecked = (jobType: JobType | null): boolean => {
    return checkedJobIds.includes(jobType?.id ?? '');
  };

  const columns: DataTableColumn<PersonJobType>[] = [
    {
      accessor: 'showTypeMinified.showDate',
      title: t(`${TRANS_PATH}.DATE`),
      cellsStyle: () => ({
        verticalAlign: 'top',
      }),
      render: (record) => <Date date={record.showTypeMinified.showDate} />,
    },
    {
      accessor: 'showTypeMinified.tourName',
      title: t(`${TRANS_PATH}.SHOW`),
      render: (record) => <ShowTableCell show={record.showTypeMinified} />,
    },
    {
      accessor: 'jobs.tourProd.isConfirmed',
      title: t(`${TRANS_PATH}.TOUR_PROD`),
      render: (record) => (
        <UnconfirmedItem
          jobType={record.jobs.tourProd}
          isChecked={isChecked(record.jobs.tourProd)}
          onCheckboxChange={onCheckJobId}
          permissionName="ADDRESSBOOK_TOURREP_JOBREQUEST_CONFIRMATION"
          isEditingOwnUser={isEditingOwnUser}
        />
      ),
    },
    {
      accessor: 'jobs.tourRep.isConfirmed',
      title: t(`${TRANS_PATH}.TOUR_REP`),
      render: (record) => (
        <UnconfirmedItem
          jobType={record.jobs.tourRep}
          isChecked={isChecked(record.jobs.tourRep)}
          onCheckboxChange={onCheckJobId}
          permissionName="ADDRESSBOOK_TOURREP_JOBREQUEST_CONFIRMATION"
          isEditingOwnUser={isEditingOwnUser}
        />
      ),
    },
    {
      accessor: 'jobs.tourAcc.isConfirmed',
      title: t(`${TRANS_PATH}.TOUR_ACC`),
      render: (record) => (
        <UnconfirmedItem
          jobType={record.jobs.tourAcc}
          isChecked={isChecked(record.jobs.tourAcc)}
          onCheckboxChange={onCheckJobId}
          permissionName="ADDRESSBOOK_TOURREP_JOBREQUEST_CONFIRMATION"
          isEditingOwnUser={isEditingOwnUser}
        />
      ),
    },
    {
      accessor: 'jobs.tourSec.isConfirmed',
      title: t(`${TRANS_PATH}.TOUR_SEC`),
      render: (record) => (
        <UnconfirmedItem
          jobType={record.jobs.tourSec}
          isChecked={isChecked(record.jobs.tourSec)}
          onCheckboxChange={onCheckJobId}
          permissionName="ADDRESSBOOK_TOURREP_JOBREQUEST_CONFIRMATION"
          isEditingOwnUser={isEditingOwnUser}
        />
      ),
    },
    {
      accessor: 'jobs.tourVip.isConfirmed',
      title: t(`${TRANS_PATH}.TOUR_VIP`),
      render: (record) => (
        <UnconfirmedItem
          jobType={record.jobs.tourVip}
          isChecked={isChecked(record.jobs.tourVip)}
          onCheckboxChange={onCheckJobId}
          permissionName="ADDRESSBOOK_TOURREP_JOBREQUEST_CONFIRMATION"
          isEditingOwnUser={isEditingOwnUser}
        />
      ),
    },
    {
      accessor: 'jobs.localProd.isConfirmed',
      title: t(`${TRANS_PATH}.LOCAL_PROD`),
      render: (record) => (
        <UnconfirmedItem
          jobType={record.jobs.localProd}
          isChecked={isChecked(record.jobs.localProd)}
          onCheckboxChange={onCheckJobId}
          permissionName="ADDRESSBOOK_SELFPROMOTE_JOBREQUEST_CONFIRMATION"
          isEditingOwnUser={isEditingOwnUser}
        />
      ),
    },
    {
      accessor: 'jobs.localTicketing.isConfirmed',
      title: t(`${TRANS_PATH}.LOCAL_TICKETING`),
      render: (record) => (
        <UnconfirmedItem
          jobType={record.jobs.localTicketing}
          isChecked={isChecked(record.jobs.localTicketing)}
          onCheckboxChange={onCheckJobId}
          permissionName="ADDRESSBOOK_SELFPROMOTE_JOBREQUEST_CONFIRMATION"
          isEditingOwnUser={isEditingOwnUser}
        />
      ),
    },
    {
      accessor: 'jobs.localRepAcc.isConfirmed',
      title: t(`${TRANS_PATH}.LOCAL_REP_ACC`),
      render: (record) => (
        <UnconfirmedItem
          jobType={record.jobs.localRepAcc}
          isChecked={isChecked(record.jobs.localRepAcc)}
          onCheckboxChange={onCheckJobId}
          permissionName="ADDRESSBOOK_SELFPROMOTE_JOBREQUEST_CONFIRMATION"
          isEditingOwnUser={isEditingOwnUser}
        />
      ),
    },
  ];

  return { columns };
};
